import { useCallback, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWebsocket } from "@finitless/shared/contexts";
import { usePostHog } from "@finitless/shared/hooks";
import { useUserStore } from "@finitless/shared/stores";
import { useSmartPagination } from "./useSmartPagination";
import {
  STATUS_GROUPS,
  getDeliveryDatetimeRangeBoundsFromWhere,
  getOrderDeliveryTimeMs,
} from "@finitless/shared/orderConstants";

/**
 * Hook for handling real-time order updates via websockets
 * Integrates with React Query for cache updates
 */
export const useOrderSocketUpdates = (options = {}) => {
  const queryClient = useQueryClient();
  const { isConnected, registerEventListener, unregisterEventListener } =
    useWebsocket();
  const [listenersSetup, setListenersSetup] = useState(false);
  const hookId = useRef(`useOrderSocketUpdates-${Date.now()}-${Math.random()}`);
  const { trackActivation } = usePostHog();
  const firstOrderTrackedRef = useRef(new Set());
  const user = useUserStore((state) => state.user);

  // Smart pagination integration
  const smartPagination = useSmartPagination(["orders"], {
    itemsPerPage: options.itemsPerPage || 12,
    onNewItemAdded: options.onNewItemAdded,
    onItemUpdated: options.onItemUpdated,
    onItemRemoved: options.onItemRemoved,
  });

  /**
   * Helper function to determine if an order should be in a query based on its where clause
   */
  const orderBelongsToQuery = useCallback((order, queryParams) => {
    if (!queryParams?.where) return true; // If no where clause, include all orders

    try {
      const whereClause =
        typeof queryParams.where === "string"
          ? JSON.parse(queryParams.where)
          : queryParams.where;

      // Check conversation_id filter (required for all queries)
      const hasConversationId = order.conversation_id != null;
      if (!hasConversationId) return false;

      // Check status filter
      const statusCondition = whereClause.conditions?.find(
        (cond) => cond.attribute === "status",
      );

      if (statusCondition && statusCondition.value) {
        const allowedStatuses = Array.isArray(statusCondition.value)
          ? statusCondition.value
          : [statusCondition.value];

        // Check if order status is in allowed statuses
        if (!allowedStatuses.includes(order.status)) {
          return false;
        }
      }

      // Check business_id filter if present
      const businessCondition = whereClause.conditions?.find(
        (cond) => cond.attribute === "business_id",
      );

      if (businessCondition && businessCondition.value) {
        if (order.business_id !== businessCondition.value) {
          return false;
        }
      }

      const deliveryBounds =
        getDeliveryDatetimeRangeBoundsFromWhere(whereClause);
      if (deliveryBounds) {
        const deliveryMs = getOrderDeliveryTimeMs(order);
        if (deliveryMs == null || Number.isNaN(deliveryMs)) {
          return false;
        }
        if (
          deliveryMs < deliveryBounds.startMs ||
          deliveryMs > deliveryBounds.endMs
        ) {
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error parsing where clause:", error);
      return true; // On error, include the order to be safe
    }
  }, []);

  /**
   * Get the status filter tab for an order based on its status
   */
  const getStatusFilterForOrder = useCallback((orderStatus) => {
    if (
      STATUS_GROUPS.pending.includes(orderStatus) ||
      STATUS_GROUPS.inProgress.includes(orderStatus)
    ) {
      return "preparing";
    }
    if (STATUS_GROUPS.completed.includes(orderStatus)) {
      return "completed";
    }
    if (STATUS_GROUPS.cancelled.includes(orderStatus)) {
      return "cancelled";
    }
    return "all";
  }, []);

  /**
   * Update order in cache, moving it between tabs based on status
   */
  const updateOrderInCache = useCallback(
    (updatedOrder) => {
      // Get all order queries
      const allOrderQueries = queryClient.getQueriesData({
        queryKey: ["orders"],
      });

      allOrderQueries.forEach(([queryKey, oldData]) => {
        if (!oldData?.orders) return;

        const queryParams = queryKey[1]; // params is the second element in ['orders', params]
        const orderBelongs = orderBelongsToQuery(updatedOrder, queryParams);
        const orderExists = oldData.orders.some(
          (order) => order.id === updatedOrder.id,
        );

        if (orderBelongs) {
          // Order should be in this query - update or add it
          if (orderExists) {
            // Update existing order and maintain order
            const updatedOrders = oldData.orders.map((order) =>
              order.id === updatedOrder.id
                ? { ...order, ...updatedOrder }
                : order,
            );

            // Re-sort to maintain descending order by ID
            updatedOrders.sort((a, b) => b.id - a.id);

            queryClient.setQueryData(queryKey, {
              ...oldData,
              orders: updatedOrders,
            });
          } else {
            // Add new order in the correct position based on ID (descending order)
            const updatedOrders = [...oldData.orders, updatedOrder];
            // Sort by ID descending (higher ID first)
            updatedOrders.sort((a, b) => b.id - a.id);

            queryClient.setQueryData(queryKey, {
              ...oldData,
              orders: updatedOrders,
              pagination: {
                ...oldData.pagination,
                total: (oldData.pagination?.total || 0) + 1,
              },
            });
          }
        } else {
          // Order should NOT be in this query - remove it if it exists
          if (orderExists) {
            queryClient.setQueryData(queryKey, {
              ...oldData,
              orders: oldData.orders.filter(
                (order) => order.id !== updatedOrder.id,
              ),
              pagination: {
                ...oldData.pagination,
                total: Math.max(0, (oldData.pagination?.total || 0) - 1),
              },
            });
          }
        }
      });

      // Also update individual order queries if they exist
      queryClient.setQueryData(["order", updatedOrder.id], updatedOrder);
    },
    [queryClient, orderBelongsToQuery, getStatusFilterForOrder],
  );

  /**
   * Add new order to cache (only to matching queries)
   */
  const addOrderToCache = useCallback(
    (newOrder) => {
      // Get all order queries to selectively update
      const allOrderQueries = queryClient.getQueriesData({
        queryKey: ["orders"],
      });

      allOrderQueries.forEach(([queryKey, oldData]) => {
        if (!oldData?.orders) return;

        const queryParams = queryKey[1];
        const orderBelongs = orderBelongsToQuery(newOrder, queryParams);

        // Only add the order to queries where it actually belongs
        if (!orderBelongs) return;

        // Check if order already exists to avoid duplicates
        const existingOrder = oldData.orders.find(
          (order) => order.id === newOrder.id,
        );
        if (existingOrder) return;

        // Add new order in the correct position based on ID (descending order)
        const updatedOrders = [newOrder, ...oldData.orders];
        updatedOrders.sort((a, b) => b.id - a.id);

        queryClient.setQueryData(queryKey, {
          ...oldData,
          orders: updatedOrders,
          pagination: {
            ...oldData.pagination,
            total: (oldData.pagination?.total || 0) + 1,
          },
        });
      });

      // Also cache the individual order
      queryClient.setQueryData(["order", newOrder.id], newOrder);

      // Update smart pagination
      smartPagination.handleNewItemAdded(newOrder);

      // Invalidate count queries to refresh totals
      queryClient.invalidateQueries({ queryKey: ["reports", "orders"] });
      queryClient.invalidateQueries({ queryKey: ["reports", "sales"] });
      queryClient.invalidateQueries({ queryKey: ["ai", "metrics", "orders"] });
    },
    [queryClient, orderBelongsToQuery, smartPagination],
  );

  /**
   * Handle new order registration
   */
  const handleNewOrder = useCallback(
    (orderData) => {
      // Filter by conversation_id (only AI-generated orders)
      if (!orderData.conversation_id) {
        return;
      }

      // Track first bot order for Time-to-Value metric
      const businessId = orderData.business_id;

      if (!user?.id) {
        addOrderToCache(orderData);
        return;
      }

      const firstOrderKey = `first_bot_order_${user.id}`;

      if (!firstOrderTrackedRef.current.has(user.id)) {
        // Check if we've already tracked this in localStorage (persists across sessions)
        const alreadyTracked = localStorage.getItem(firstOrderKey);

        if (!alreadyTracked) {
          trackActivation.firstBotOrder({
            order_id: orderData.id,
            user_id: user.id,
            business_id: businessId,
            conversation_id: orderData.conversation_id,
            signup_date: user.created_at,
          });

          // Mark as tracked
          localStorage.setItem(firstOrderKey, "true");
          firstOrderTrackedRef.current.add(user.id);
        } else {
          firstOrderTrackedRef.current.add(user.id);
        }
      }

      addOrderToCache(orderData);
    },
    [addOrderToCache, trackActivation, user?.id, user?.created_at],
  );

  /**
   * Handle order updates
   */
  const handleOrderUpdate = useCallback(
    (orderData) => {
      // Filter by conversation_id (only AI-generated orders)
      if (!orderData.conversation_id) {
        return;
      }

      updateOrderInCache(orderData);
      smartPagination.handleItemUpdated(orderData);

      // Invalidate report and AI metric queries so stats refresh
      queryClient.invalidateQueries({ queryKey: ["reports", "orders"] });
      queryClient.invalidateQueries({ queryKey: ["reports", "sales"] });
      queryClient.invalidateQueries({ queryKey: ["ai", "metrics", "orders"] });
    },
    [updateOrderInCache, smartPagination, queryClient],
  );

  /**
   * Handle new messages
   */
  const handleNewMessage = useCallback(
    (messageData) => {
      // Invalidate order messages for the specific order
      if (messageData.order_id) {
        queryClient.invalidateQueries({
          queryKey: ["orderMessages", messageData.order_id],
        });
      }
    },
    [queryClient],
  );

  // Use refs to maintain stable references
  const handleNewOrderRef = useRef(handleNewOrder);
  const handleOrderUpdateRef = useRef(handleOrderUpdate);
  const handleNewMessageRef = useRef(handleNewMessage);

  // Update refs when handlers change
  useEffect(() => {
    handleNewOrderRef.current = handleNewOrder;
    handleOrderUpdateRef.current = handleOrderUpdate;
    handleNewMessageRef.current = handleNewMessage;
  }, [handleNewOrder, handleOrderUpdate, handleNewMessage]);

  /**
   * Setup socket listeners using centralized event system
   */
  const setupSocketListeners = useCallback(() => {
    if (!isConnected || listenersSetup) return;

    // Use wrapper functions that always delegate to the current ref value
    registerEventListener(
      "orders_register",
      (data) => handleNewOrderRef.current(data),
      hookId.current,
    );
    registerEventListener(
      "update_order",
      (data) => handleOrderUpdateRef.current(data),
      hookId.current,
    );
    registerEventListener(
      "message",
      (data) => handleNewMessageRef.current(data),
      hookId.current,
    );

    setListenersSetup(true);
  }, [isConnected, listenersSetup, registerEventListener]);

  /**
   * Cleanup socket listeners using centralized event system
   */
  const cleanupSocketListeners = useCallback(() => {
    if (!listenersSetup) return;

    // Unregister from centralized event system
    unregisterEventListener("orders_register", hookId.current);
    unregisterEventListener("update_order", hookId.current);
    unregisterEventListener("message", hookId.current);

    setListenersSetup(false);
  }, [listenersSetup, unregisterEventListener]);

  // Setup listeners when connected
  useEffect(() => {
    if (isConnected && !listenersSetup) {
      // Use wrapper functions that always delegate to the current ref value.
      // This ensures the registered listener always calls the latest handler.
      registerEventListener(
        "orders_register",
        (data) => handleNewOrderRef.current(data),
        hookId.current,
      );
      registerEventListener(
        "update_order",
        (data) => handleOrderUpdateRef.current(data),
        hookId.current,
      );
      registerEventListener(
        "message",
        (data) => handleNewMessageRef.current(data),
        hookId.current,
      );

      setListenersSetup(true);
    }
  }, [isConnected, registerEventListener]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (listenersSetup) {
        // Unregister from centralized event system
        unregisterEventListener("orders_register", hookId.current);
        unregisterEventListener("update_order", hookId.current);
        unregisterEventListener("message", hookId.current);

        setListenersSetup(false);
      }
    };
  }, [listenersSetup, unregisterEventListener]);

  return {
    isConnected,
    setupSocketListeners,
    cleanupSocketListeners,
    handleNewOrder,
    handleOrderUpdate,
    handleNewMessage,
    // Smart pagination integration
    smartPagination,
  };
};

export default useOrderSocketUpdates;
